import {
  FilterBuilderFieldDefinition,
  FilterBuilderFieldsType,
} from "@validereinc/common-components";
import {
  AttributeDataType,
  FormSchemaQuestionDisplayType,
  FormSchemaQuestionType,
} from "@validereinc/domain";
import { Forms } from "@validereinc/domain-controllers";
import { ComparisonOperators, LogicalOperators } from "@validereinc/utilities";

const { getPickListValuesWithConditionalOverrides } = Forms.logic;

/**
 * Unwraps `{ $eq: primitive }` wrappers on numeric fields so FilterBuilder
 * can represent exact match with the `=` operator.
 *
 * @example
 * unwrapNumericEqCondition({ $eq: 3 }, "wind_speed", new Set(["wind_speed"])) // => 3
 */
const unwrapNumericEqCondition = (
  value: unknown,
  fieldKey: string,
  numericFieldKeys: Set<string>
): unknown => {
  if (!numericFieldKeys.has(fieldKey)) return value;

  if (
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    ComparisonOperators.eq in value &&
    Object.keys(value).length === 1
  ) {
    return (value as Record<typeof ComparisonOperators.eq, unknown>)[
      ComparisonOperators.eq
    ];
  }

  return value;
};

/**
 * Returns question IDs whose FilterBuilder field definitions use numeric data types.
 */
export const getNumericFieldKeys = (
  fields: FilterBuilderFieldsType
): Set<string> =>
  new Set(
    Object.entries(fields)
      .filter(
        ([, definition]) =>
          definition.data_type === AttributeDataType.NUMBER ||
          definition.data_type === AttributeDataType.INTEGER
      )
      .map(([key]) => key)
  );

/**
 * Builds FilterBuilder field definitions from prior form template questions.
 */
export const buildFilterBuilderFieldsFromQuestions = (
  questions: FormSchemaQuestionDisplayType[]
): FilterBuilderFieldsType =>
  Object.fromEntries(
    questions
      .filter((question) => "question_id" in question)
      .flatMap((question) => {
        let definition: FilterBuilderFieldDefinition | undefined;

        if (question.type === FormSchemaQuestionType.QUESTION) {
          if (
            question.data_type === AttributeDataType.BOOLEAN ||
            question.data_type === AttributeDataType.INTEGER ||
            question.data_type === AttributeDataType.NUMBER
          ) {
            definition = {
              data_type: question.data_type,
              display_name: question.prompt,
            };
          } else if (question.data_type === AttributeDataType.DATE) {
            definition = {
              data_type: AttributeDataType.DATE,
              display_name: question.prompt,
              format: "yyyy-MM-dd",
            };
          } else if (
            question.data_type === AttributeDataType.PICK_LIST ||
            question.data_type === AttributeDataType.MULTI_PICK_LIST
          ) {
            definition = {
              data_type: AttributeDataType.PICK_LIST,
              display_name: question.prompt,
              pick_list_options:
                getPickListValuesWithConditionalOverrides(question),
            };
          }
        } else if (question.type === FormSchemaQuestionType.MEASUREMENT) {
          definition = {
            data_type: AttributeDataType.NUMBER,
            display_name: question.prompt,
          };
        }

        if (!definition) return [];

        return [[question.question_id, definition]];
      })
  );

/**
 * Normalizes a filter object so that each condition within `$or`/`$and` arrays
 * has at most one key, as required by FilterBuilder. Multi-key conditions are
 * implicitly `$and` conjunctions and are wrapped in a `$and` sub-group.
 *
 * Numeric field values stored as `{ $eq: primitive }` are unwrapped to primitives
 * so FilterBuilder shows the `=` operator.
 */
export const normalizeFilterForFilterBuilder = (
  filter: Record<string, unknown>,
  numericFieldKeys: Set<string> = new Set()
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(filter)) {
    if (
      (key === LogicalOperators.or || key === LogicalOperators.and) &&
      Array.isArray(value)
    ) {
      result[key] = value.map((condition: Record<string, unknown>) => {
        const normalized = normalizeFilterForFilterBuilder(
          condition,
          numericFieldKeys
        );
        const keys = Object.keys(normalized);

        if (keys.length <= 1) return normalized;

        return {
          [LogicalOperators.and]: keys.map((conditionKey) => ({
            [conditionKey]: normalized[conditionKey],
          })),
        };
      });
    } else {
      result[key] = unwrapNumericEqCondition(value, key, numericFieldKeys);
    }
  }

  return result;
};

/**
 * Denormalizes a filter object produced by FilterBuilder back into the format
 * expected by the backend. Inlines `$and` sub-groups that were introduced by
 * {@link normalizeFilterForFilterBuilder} by merging their single-key entries
 * into one multi-key condition object.
 *
 * Any remaining `{ $eq: primitive }` wrappers on numeric fields are unwrapped
 * to primitives before persisting.
 */
export const denormalizeFilterFromFilterBuilder = (
  filter: Record<string, unknown>,
  numericFieldKeys: Set<string> = new Set()
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(filter)) {
    if (
      (key === LogicalOperators.or || key === LogicalOperators.and) &&
      Array.isArray(value)
    ) {
      result[key] = value.map((condition: Record<string, unknown>) => {
        const denormalized = denormalizeFilterFromFilterBuilder(
          condition,
          numericFieldKeys
        );
        const andValue = denormalized[LogicalOperators.and];

        if (!Array.isArray(andValue)) return denormalized;

        const merged: Record<string, unknown> = {};

        for (const entry of andValue as Array<Record<string, unknown>>) {
          Object.assign(
            merged,
            denormalizeFilterFromFilterBuilder(entry, numericFieldKeys)
          );
        }

        const { [LogicalOperators.and]: _, ...rest } = denormalized;
        return { ...rest, ...merged };
      });
    } else {
      result[key] = unwrapNumericEqCondition(value, key, numericFieldKeys);
    }
  }

  return result;
};

/**
 * Returns whether a conditions object contains at least one question field rule.
 */
export const hasConditionFields = (
  conditions: Record<string, unknown>
): boolean => {
  for (const [key, value] of Object.entries(conditions)) {
    if (key === LogicalOperators.or || key === LogicalOperators.and) {
      if (
        Array.isArray(value) &&
        value.some((entry) =>
          hasConditionFields(entry as Record<string, unknown>)
        )
      ) {
        return true;
      }
    } else {
      return true;
    }
  }

  return false;
};

/**
 * Returns whether a conditions object has no rules (e.g. after clearing FilterBuilder).
 */
export const isConditionsEmpty = (
  conditions: Record<string, unknown>
): boolean => !hasConditionFields(conditions);
